/**
 * Quality Manager surface.
 *
 * Headline figures are escape rate and false-reject rate, not accuracy
 * (spec 1.6). Every metric separates VALUE / FROM / DATA QUALITY, and a null
 * is rendered as "Not available + reason" — never as 0%.
 */
import { useState } from 'react';
import { api, qs } from '../api/client';
import { useApi } from '../hooks/useApi';
import type { RateMetrics, ShadowReport, Station } from '../api/types';
import { Card, Empty, ErrorBox, Loading, Metric, Pct, Unavailable } from '../components/Metric';
import { ConfusionMatrix } from '../components/charts';

const WINDOWS = [
  { h: 8, label: '8h' },
  { h: 24, label: '1d' },
  { h: 72, label: '3d' },
  { h: 168, label: '7d' },
];

export function QualityPage() {
  const [hours, setHours] = useState(24);
  const from = new Date(Date.now() - hours * 3600_000).toISOString();
  const to = new Date().toISOString();

  const { data: stations } = useApi<Station[]>(() => api.get<Station[]>('/api/v1/stations'), []);
  const [stationId, setStationId] = useState<string>('');
  const machineId = stations?.find((s) => s.id === stationId)?.machine?.id ?? undefined;

  const rates = useApi<{ rates: RateMetrics }>(
    () => api.get(`/api/v1/metrics/rates${qs({ from, to, machineId })}`),
    [hours, machineId],
  );

  const byDefect = useApi<{
    defects: Array<{
      defectName: string;
      total: number;
      labelled: number;
      escapes: number;
      falseRejects: number;
      escapeRatePct: number | null;
      falseRejectRatePct: number | null;
    }>;
    note: string;
  }>(() => api.get(`/api/v1/metrics/rates/by-defect${qs({ from, to })}`), [hours]);

  const report = useApi<ShadowReport>(
    () =>
      stationId
        ? api.get<ShadowReport>(`/api/v1/stations/${stationId}/shadow-report/latest`)
        : Promise.reject(new Error('no station')),
    [stationId],
  );

  const r = rates.data?.rates;
  const labelled = (r?.labelledEvents ?? 0) > 0;

  return (
    <>
      <div className="btn-row" style={{ marginBottom: 16 }}>
        <div className="seg" role="group" aria-label="Time window">
          {WINDOWS.map((w) => (
            <button key={w.h} className={hours === w.h ? 'on' : ''} onClick={() => setHours(w.h)}>
              {w.label}
            </button>
          ))}
        </div>
        <label htmlFor="quality-station" style={{ fontSize: 12, color: 'var(--ink-2)', fontWeight: 600 }}>
          Station
        </label>
        <select
          id="quality-station"
          style={{ maxWidth: 300 }}
          value={stationId}
          onChange={(e) => setStationId(e.target.value)}
        >
          <option value="">All stations</option>
          {stations?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      {rates.error && <ErrorBox message={rates.error} />}
      {rates.loading && <Loading />}

      <div className="grid grid-4">
        <Metric
          label="Escape rate"
          tone={r?.escapeRatePct == null ? 'default' : r.escapeRatePct > 2 ? 'danger' : r.escapeRatePct > 0.5 ? 'warn' : 'ok'}
          value={r?.escapeRatePct != null ? r.escapeRatePct.toFixed(2) : undefined}
          unit={r?.escapeRatePct != null ? '%' : undefined}
          sub={`${r?.escapes ?? 0} real defects passed as good`}
          from={`From ${r?.labelledEvents ?? 0} human-labelled cycles`}
        >
          <Unavailable reason="Human labels required. An escape rate invented from unlabelled data would be worse than none." />
        </Metric>

        <Metric
          label="False-reject rate"
          tone={r?.falseRejectRatePct == null ? 'default' : r.falseRejectRatePct > 1 ? 'warn' : 'ok'}
          value={r?.falseRejectRatePct != null ? r.falseRejectRatePct.toFixed(2) : undefined}
          unit={r?.falseRejectRatePct != null ? '%' : undefined}
          sub={`${r?.falseRejects ?? 0} good parts flagged`}
          from={`From ${r?.labelledEvents ?? 0} human-labelled cycles`}
        >
          <Unavailable reason="Human labels required in this window." />
        </Metric>

        <Metric
          label="Detection rate"
          value={r?.detectionRatePct != null ? r.detectionRatePct.toFixed(1) : undefined}
          unit={r?.detectionRatePct != null ? '%' : undefined}
          sub="Share of real defects caught"
          from="Labelled defect cycles"
        >
          <Unavailable reason="Needs labelled defect cycles." />
        </Metric>

        <Metric
          label="AI–inspector agreement"
          value={r?.agreementPct != null ? r.agreementPct.toFixed(1) : undefined}
          unit={r?.agreementPct != null ? '%' : undefined}
          sub={`${r?.labelledEvents ?? 0} labelled of ${r?.totalEvents ?? 0} cycles`}
          from="Model output vs human label"
        >
          <Unavailable reason="No labelled cycles yet." />
        </Metric>
      </div>

      {!labelled && (
        <div className="callout info" style={{ marginTop: 14 }}>
          <strong>No human-labelled cycles in this window, so no rate can be computed.</strong>
          Rates require the inspector scrap log or operator overrides — they are
          never inferred from model output alone.
        </div>
      )}
      {labelled && r?.note && (
        <div className="callout info" style={{ marginTop: 14 }}>{r.note}</div>
      )}

      <div className="grid grid-2" style={{ marginTop: 14 }}>
        <Card title="Escapes and false rejects by defect" note={byDefect.data?.note}>
          {!byDefect.data || byDefect.data.defects.length === 0 ? (
            <Empty title="No labelled cycles in this window" />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Defect</th>
                    <th className="num">Cycles</th>
                    <th className="num">Escapes</th>
                    <th className="num">False rejects</th>
                  </tr>
                </thead>
                <tbody>
                  {byDefect.data.defects.map((d) => (
                    <tr key={d.defectName}>
                      <td style={{ fontWeight: 600 }}>{d.defectName}</td>
                      <td className="num">{d.total}</td>
                      <td className="num">
                        <Pct value={d.escapeRatePct} unavailableReason="unlabelled" />
                        <span className="dim"> ({d.escapes})</span>
                      </td>
                      <td className="num">
                        <Pct value={d.falseRejectRatePct} unavailableReason="unlabelled" />
                        <span className="dim"> ({d.falseRejects})</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card
          title="Shadow-mode accuracy report"
          actions={
            <select
              aria-label="Station for shadow report"
              style={{ maxWidth: 220 }}
              value={stationId}
              onChange={(e) => setStationId(e.target.value)}
            >
              <option value="">Select a station…</option>
              {stations?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          }
          note={
            report.data
              ? `Compared against the inspector's scrap log${report.data.humanScrapLogRef ? ` (${report.data.humanScrapLogRef})` : ''}. ${report.data.unlabelledCycles} cycles had no human label and are excluded from the rates — never assumed good.`
              : 'Select a station to load its most recent shadow report.'
          }
        >
          {!stationId ? (
            <Empty title="No station selected" />
          ) : report.error ? (
            <Empty title="No shadow report yet">
              Run the station in SHADOW mode with human labels, then generate a
              report from the station view.
            </Empty>
          ) : !report.data ? (
            <Loading />
          ) : (
            <>
              <ConfusionMatrix
                fn={report.data.falseNegatives}
                fp={report.data.falsePositives}
                tp={report.data.truePositives}
                tn={report.data.trueNegatives}
              />
              <div className="grid grid-3" style={{ marginTop: 14 }}>
                <Metric label="Escape rate" value={<Pct value={report.data.escapeRatePct} />} />
                <Metric label="False reject" value={<Pct value={report.data.falseRejectPct} />} />
                <Metric label="Agreement" value={<Pct value={report.data.agreementPct} />} />
              </div>
              <div className={`callout ${report.data.gate.met ? 'info' : 'warn'}`} style={{ marginTop: 14, marginBottom: 0 }}>
                <strong>{report.data.gate.label}</strong>
                {report.data.gate.met
                  ? 'This station meets the recommended gate for going active. '
                  : 'This station does not yet meet the recommended gate for going active. '}
                Source: {report.data.gate.source}. This gate is illustrative
                guidance from the pilot strategy, not a contractual threshold —
                set your own against your escape-rate tolerance.
              </div>
            </>
          )}
        </Card>
      </div>
    </>
  );
}
